import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { courseAPI, userAPI } from '../../services/api';
import Modal from '../../components/Modal';
import { LoadingSpinner, EmptyState } from '../../components/UI';

const AdminCourses = () => {
  const [courses, setCourses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, course: null });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([courseAPI.list(), userAPI.trainers()]);
      setCourses(c.data);
      setTrainers(t.data);
    } catch (e) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete course and all related enrollments?')) return;
    try {
      await courseAPI.delete(id);
      toast.success('Course deleted');
      fetchAll();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1>Courses</h1>
          <p className="subtitle">{courses.length} total</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal({ open: true, course: null })}>
          + New course
        </button>
      </div>

      {loading ? <LoadingSpinner /> : courses.length === 0 ? (
        <EmptyState icon="📚" title="No courses yet" message="Create your first course to get started" />
      ) : (
        <div className="grid grid-3">
          {courses.map((c) => (
            <div key={c._id} className="course-card">
              <div className="thumb">{c.title.charAt(0)}</div>
              <div className="body">
                <span className="category">{c.category}</span>
                <h3>{c.title}</h3>
                <p className="desc">{c.description}</p>
                <div className="meta">
                  <span>👤 {c.trainer?.name || 'Unassigned'}</span>
                  <span>{c.modules?.length || 0} modules</span>
                </div>
                <div className="flex gap-1 mt-2">
                  <button className="btn btn-secondary btn-sm" onClick={() => setModal({ open: true, course: c })} style={{ flex: 1 }}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c._id)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <CourseModal
        open={modal.open}
        course={modal.course}
        trainers={trainers}
        onClose={() => setModal({ open: false, course: null })}
        onSaved={fetchAll}
      />
    </div>
  );
};

const CourseModal = ({ open, course, trainers, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (course) {
      setForm({
        title: course.title,
        description: course.description,
        category: course.category,
        level: course.level,
        price: course.price,
        duration: course.duration,
        trainer: course.trainer?._id || course.trainer,
        tags: (course.tags || []).join(', '),
        modules: course.modules || [],
        quiz: course.quiz || { title: '', passingScore: 60, questions: [] },
      });
    } else {
      setForm({
        title: '', description: '', category: '', level: 'Beginner',
        price: 0, duration: 0, trainer: '', tags: '',
        modules: [], quiz: { title: '', passingScore: 60, questions: [] },
      });
    }
  }, [course, open]);

  const addModule = () => {
    setForm({
      ...form,
      modules: [...(form.modules || []), { title: '', description: '', order: form.modules.length + 1, lessons: [] }],
    });
  };
  const updateModule = (i, field, value) => {
    const mods = [...form.modules];
    mods[i][field] = value;
    setForm({ ...form, modules: mods });
  };
  const removeModule = (i) => {
    setForm({ ...form, modules: form.modules.filter((_, idx) => idx !== i) });
  };
  const addLesson = (mi) => {
    const mods = [...form.modules];
    mods[mi].lessons.push({ title: '', content: '', duration: 0, order: mods[mi].lessons.length + 1 });
    setForm({ ...form, modules: mods });
  };
  const updateLesson = (mi, li, field, value) => {
    const mods = [...form.modules];
    mods[mi].lessons[li][field] = value;
    setForm({ ...form, modules: mods });
  };
  const removeLesson = (mi, li) => {
    const mods = [...form.modules];
    mods[mi].lessons = mods[mi].lessons.filter((_, idx) => idx !== li);
    setForm({ ...form, modules: mods });
  };

  const addQuestion = () => {
    setForm({
      ...form,
      quiz: {
        ...form.quiz,
        questions: [...(form.quiz?.questions || []), { question: '', options: ['', '', '', ''], correctAnswer: 0 }],
      },
    });
  };
  const updateQuestion = (i, field, value) => {
    const qs = [...form.quiz.questions];
    qs[i][field] = value;
    setForm({ ...form, quiz: { ...form.quiz, questions: qs } });
  };
  const updateOption = (qi, oi, value) => {
    const qs = [...form.quiz.questions];
    qs[qi].options[oi] = value;
    setForm({ ...form, quiz: { ...form.quiz, questions: qs } });
  };
  const removeQuestion = (i) => {
    setForm({ ...form, quiz: { ...form.quiz, questions: form.quiz.questions.filter((_, idx) => idx !== i) } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()) : [],
        price: Number(form.price),
        duration: Number(form.duration),
      };
      if (course) await courseAPI.update(course._id, payload);
      else await courseAPI.create(payload);
      toast.success(course ? 'Course updated' : 'Course created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={course ? 'Edit course' : 'New course'} size="lg">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title</label>
          <input className="input" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea className="textarea" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
        </div>
        <div className="grid grid-2">
          <div className="form-group">
            <label>Category</label>
            <input className="input" value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} required placeholder="e.g. Web Development" />
          </div>
          <div className="form-group">
            <label>Level</label>
            <select className="select" value={form.level || 'Beginner'} onChange={(e) => setForm({ ...form, level: e.target.value })}>
              <option>Beginner</option>
              <option>Intermediate</option>
              <option>Advanced</option>
            </select>
          </div>
          <div className="form-group">
            <label>Price ($)</label>
            <input className="input" type="number" value={form.price || 0} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Duration (hrs)</label>
            <input className="input" type="number" value={form.duration || 0} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label>Trainer</label>
          <select className="select" value={form.trainer || ''} onChange={(e) => setForm({ ...form, trainer: e.target.value })} required>
            <option value="">Select a trainer</option>
            {trainers.map((t) => <option key={t._id} value={t._id}>{t.name} — {t.email}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Tags (comma separated)</label>
          <input className="input" value={form.tags || ''} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="react, mongodb, node" />
        </div>

        <h4 className="mt-3 mb-2">Modules & Lessons</h4>
        {(form.modules || []).map((m, mi) => (
          <div key={mi} className="card mb-2" style={{ background: 'var(--bg)' }}>
            <div className="flex gap-1 mb-2">
              <input className="input" placeholder="Module title" value={m.title} onChange={(e) => updateModule(mi, 'title', e.target.value)} />
              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeModule(mi)}>×</button>
            </div>
            {m.lessons.map((l, li) => (
              <div key={li} className="flex gap-1 mb-2">
                <input className="input" placeholder="Lesson title" value={l.title} onChange={(e) => updateLesson(mi, li, 'title', e.target.value)} />
                <input className="input" type="number" placeholder="Min" value={l.duration} onChange={(e) => updateLesson(mi, li, 'duration', Number(e.target.value))} style={{ maxWidth: 80 }} />
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeLesson(mi, li)}>×</button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => addLesson(mi)}>+ Add lesson</button>
          </div>
        ))}
        <button type="button" className="btn btn-secondary btn-sm" onClick={addModule}>+ Add module</button>

        <h4 className="mt-3 mb-2">Quiz</h4>
        <div className="grid grid-2">
          <div className="form-group">
            <label>Quiz title</label>
            <input className="input" value={form.quiz?.title || ''} onChange={(e) => setForm({ ...form, quiz: { ...form.quiz, title: e.target.value } })} />
          </div>
          <div className="form-group">
            <label>Passing score (%)</label>
            <input className="input" type="number" value={form.quiz?.passingScore || 60} onChange={(e) => setForm({ ...form, quiz: { ...form.quiz, passingScore: Number(e.target.value) } })} />
          </div>
        </div>
        {(form.quiz?.questions || []).map((q, qi) => (
          <div key={qi} className="card mb-2" style={{ background: 'var(--bg)' }}>
            <div className="flex gap-1 mb-2">
              <input className="input" placeholder="Question" value={q.question} onChange={(e) => updateQuestion(qi, 'question', e.target.value)} />
              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeQuestion(qi)}>×</button>
            </div>
            {q.options.map((o, oi) => (
              <div key={oi} className="flex gap-1 mb-2 items-center">
                <input type="radio" checked={q.correctAnswer === oi} onChange={() => updateQuestion(qi, 'correctAnswer', oi)} />
                <input className="input" placeholder={`Option ${oi + 1}`} value={o} onChange={(e) => updateOption(qi, oi, e.target.value)} />
              </div>
            ))}
          </div>
        ))}
        <button type="button" className="btn btn-secondary btn-sm" onClick={addQuestion}>+ Add question</button>

        <div className="flex gap-1 justify-between mt-3">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save course'}</button>
        </div>
      </form>
    </Modal>
  );
};

export default AdminCourses;
